/* eslint-disable no-console */
/**
 * Modal ÚNICO de clasificación contable (SII recibidas) — unifica los dos que había antes
 * (ClasificarFacturaModal "solo esta factura" + ClasificarProveedorModal "todo el proveedor")
 * en un solo componente con un SELECTOR DE ALCANCE visible. Ambos puntos de entrada de
 * ComprasTab llevan acá: el CHIP abre con alcance "solo esta factura"; la FILA, con alcance
 * "todo el proveedor".
 *
 * SOLO consolida UI + wiring: los handlers siguen llamando a las MISMAS funciones puras de
 * `utils/compras/clasificacionProveedor.js` (clasificarFacturaUnica / aplicarReglaProveedor /
 * volverAReglaDelProveedor / desvincularReglaProveedor). No reescribe la lógica ni el schema.
 */
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import {
  MODO,
  esSinClasificar,
  aplicarReglaProveedor,
  clasificarFacturaUnica,
  volverAReglaDelProveedor,
  desvincularReglaProveedor,
  previewDesvinculoRegla,
} from "../../utils/compras/clasificacionProveedor";
import { FECHA_CORTE } from "../../utils/estadoCuenta/saldoInicial";
import { getProveedorPorId } from "../../utils/proveedores";
import { client } from "../../utils/supabase";

export const ALCANCE = { FACTURA: "factura", PROVEEDOR: "proveedor" };

const CUENTAS_POR_GRUPO = [...new Set(CUENTAS_ER.map((c) => c.grupo))].map((grupo) => ({
  grupo,
  cuentas: CUENTAS_ER.filter((c) => c.grupo === grupo),
}));
const labelCuenta = (v) => CUENTAS_ER.find((c) => c.value === v)?.label || v;

const selectStyle = {
  width: "100%",
  height: 36,
  fontSize: "0.82rem",
  padding: "4px 10px",
  border: "1px solid #ced4da",
  borderRadius: 4,
};

const SelectorCuenta = ({ value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
    <option value="">— Seleccionar cuenta —</option>
    {CUENTAS_POR_GRUPO.map(({ grupo, cuentas }) => (
      <optgroup key={grupo} label={grupo}>
        {cuentas.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </optgroup>
    ))}
  </select>
);

const Radio = ({ name, value, checked, onChange, children, disabled = false }) => (
  <label style={{ cursor: disabled ? "not-allowed" : "pointer" }}>
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      style={{ marginRight: 4 }}
    />
    {children}
  </label>
);

const ClasificarModal = ({ doc, alcanceInicial = ALCANCE.FACTURA, onClose, onAplicada }) => {
  const {
    id: facturaId,
    folio,
    proveedorId,
    proveedorNombre,
    pais,
    cuentaEr,
    tipoContable,
    clasificacionManual,
    razonSocial,
  } = doc;

  // Sin proveedor identificado, el único alcance posible es la factura.
  const [alcance, setAlcance] = useState(proveedorId ? alcanceInicial : ALCANCE.FACTURA);
  // Naturaleza. Prefill desde la clasificación vigente de la factura.
  const [modo, setModo] = useState(tipoContable === "activo" ? MODO.ACTIVO : MODO.GASTO);
  const [cuentaER, setCuentaER] = useState(tipoContable === "activo" ? "" : cuentaEr || "");
  // Sub-elección del modo híbrido (solo alcance proveedor): cómo se clasifica ESTA factura.
  const [hibTipo, setHibTipo] = useState(MODO.GASTO);
  const [hibCuenta, setHibCuenta] = useState(cuentaEr || "");
  const [prov, setProv] = useState(null);
  const [nSinClasificar, setNSinClasificar] = useState(null);
  const [guardando, setGuardando] = useState(false);
  // Diálogo de confirmación del desvínculo (acción en masa): { revertidas, manuales } o null.
  const [desvinculoPreview, setDesvinculoPreview] = useState(null);

  useEffect(() => {
    if (!proveedorId) return undefined;
    let cancelado = false;
    getProveedorPorId(proveedorId)
      .then((p) => {
        if (cancelado || !p) return;
        setProv(p);
        // Prefill de la cuenta del híbrido desde el default viejo del proveedor, si existe.
        if (p.cuenta_er_default) setHibCuenta((prev) => prev || p.cuenta_er_default);
      })
      .catch((err) => console.error("ClasificarModal: error cargando proveedor", err));
    // N real = recibidas 2026 del proveedor sin clasificar (mismo SELECT + predicado puro
    // esSinClasificar que usa aplicarReglaProveedor). Solo para mostrar el conteo en el radio.
    client
      .from("facturas_sii_recibidas")
      .select("id, tipo_dte, fecha_emision, tipo_contable, cuenta_er, clasificacion_manual")
      .eq("pais", pais)
      .eq("proveedor_id", proveedorId)
      .gte("fecha_emision", FECHA_CORTE)
      .then(({ data, error }) => {
        if (cancelado) return;
        if (error) {
          console.error("ClasificarModal: error contando sin clasificar", error);
          setNSinClasificar(null);
        } else {
          setNSinClasificar((data || []).filter(esSinClasificar).length);
        }
      });
    return () => {
      cancelado = true;
    };
  }, [proveedorId, pais]);

  const reglaActual = prov?.clasificacion_modo;

  const cambiarAlcance = (a) => {
    setAlcance(a);
    // Híbrido no aplica a una sola factura: al pasar a alcance factura se cae a Gasto.
    if (a === ALCANCE.FACTURA && modo === MODO.HIBRIDO) setModo(MODO.GASTO);
  };

  const aplicar = async () => {
    if (alcance === ALCANCE.PROVEEDOR && modo === MODO.HIBRIDO) {
      if (hibTipo === MODO.GASTO && !hibCuenta) return toast.error("Elegí la cuenta para esta factura");
    } else if (modo === MODO.GASTO && !cuentaER) {
      return toast.error("Elegí una cuenta del ER");
    }

    setGuardando(true);
    try {
      if (alcance === ALCANCE.FACTURA) {
        // Excepción per-factura (clasificacion_manual = true): ningún automatismo la pisa.
        await clasificarFacturaUnica({ facturaId, modo, cuentaEr: modo === MODO.GASTO ? cuentaER : null });
        toast.success("Excepción aplicada a esta factura");
      } else if (modo === MODO.HIBRIDO) {
        // Marca el proveedor híbrido (no propaga) y clasifica solo la factura abierta.
        await aplicarReglaProveedor({ proveedorId, pais, modo: MODO.HIBRIDO });
        await clasificarFacturaUnica({ facturaId, modo: hibTipo, cuentaEr: hibTipo === MODO.GASTO ? hibCuenta : null });
        toast.success("Proveedor marcado como híbrido · esta factura clasificada");
      } else {
        const { propagadas } = await aplicarReglaProveedor({ proveedorId, pais, modo, cuentaEr: cuentaER });
        toast.success(
          propagadas > 0
            ? `Regla aplicada · ${propagadas} factura${propagadas === 1 ? "" : "s"} clasificada${propagadas === 1 ? "" : "s"}`
            : "Regla guardada (no había facturas sin clasificar)"
        );
      }
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarModal: error al aplicar", err);
      toast.error("No se pudo aplicar la clasificación");
    } finally {
      setGuardando(false);
    }
  };

  const volverALaRegla = async () => {
    setGuardando(true);
    try {
      await volverAReglaDelProveedor({ facturaId, proveedorId });
      toast.success(
        proveedorId ? "Factura devuelta a la regla del proveedor" : "Excepción quitada (queda sin clasificar)"
      );
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarModal: error al volver a la regla", err);
      toast.error("No se pudo quitar la excepción");
    } finally {
      setGuardando(false);
    }
  };

  // Desvincular regla del proveedor (O-3): dos pasos con confirmación.
  const pedirDesvincular = async () => {
    setGuardando(true);
    try {
      setDesvinculoPreview(await previewDesvinculoRegla({ proveedorId, pais }));
    } catch (err) {
      console.error("ClasificarModal: error al calcular el desvínculo", err);
      toast.error("No se pudo calcular el desvínculo");
    } finally {
      setGuardando(false);
    }
  };

  const confirmarDesvincular = async () => {
    setGuardando(true);
    try {
      const { revertidas, manuales } = await desvincularReglaProveedor({ proveedorId, pais });
      toast.success(
        `Regla eliminada · ${revertidas} factura${revertidas === 1 ? "" : "s"} a sin clasificar` +
          (manuales > 0
            ? ` · ${manuales} excepción${manuales === 1 ? "" : "es"} conservada${manuales === 1 ? "" : "s"}`
            : "")
      );
      onAplicada?.();
    } catch (err) {
      console.error("ClasificarModal: error al desvincular", err);
      toast.error("No se pudo eliminar la regla");
    } finally {
      setGuardando(false);
    }
  };

  const esProveedor = alcance === ALCANCE.PROVEEDOR;
  const nLabel = nSinClasificar == null ? "…" : nSinClasificar;

  return (
    <Modal isOpen toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>
        Clasificar — Folio {folio} &middot; {proveedorNombre}
      </ModalHeader>

      {desvinculoPreview ? (
        <>
          <ModalBody>
            <div style={{ fontSize: "0.85rem", lineHeight: 1.6 }}>
              Vas a desvincular la regla de <strong>{proveedorNombre}</strong>. Se devolverán{" "}
              <strong>{desvinculoPreview.revertidas}</strong> factura{desvinculoPreview.revertidas === 1 ? "" : "s"} a{" "}
              <strong>sin clasificar</strong>.
              {desvinculoPreview.manuales > 0 && (
                <>
                  {" "}
                  Se conservan <strong>{desvinculoPreview.manuales}</strong> excepción
                  {desvinculoPreview.manuales === 1 ? "" : "es"} manual{desvinculoPreview.manuales === 1 ? "" : "es"}.
                </>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              size="sm"
              onClick={() => setDesvinculoPreview(null)}
              disabled={guardando}
              style={{ marginRight: 8 }}
            >
              Cancelar
            </Button>
            <Button
              color="danger"
              size="sm"
              style={{ minWidth: 110 }}
              onClick={confirmarDesvincular}
              disabled={guardando}
            >
              {guardando ? "Desvinculando…" : "Confirmar"}
            </Button>
          </ModalFooter>
        </>
      ) : (
        <>
          <ModalBody>
            {/* Alcance — el selector que faltaba: mismo modal, dos alcances explícitos. */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 6 }}>Alcance</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: "0.82rem" }}>
                <Radio
                  name="alcance"
                  value={ALCANCE.FACTURA}
                  checked={alcance === ALCANCE.FACTURA}
                  onChange={() => cambiarAlcance(ALCANCE.FACTURA)}
                >
                  Solo esta factura
                </Radio>
                <Radio
                  name="alcance"
                  value={ALCANCE.PROVEEDOR}
                  checked={alcance === ALCANCE.PROVEEDOR}
                  onChange={() => cambiarAlcance(ALCANCE.PROVEEDOR)}
                  disabled={!proveedorId}
                >
                  {proveedorId ? (
                    <>
                      Todo el proveedor <strong>{proveedorNombre}</strong> ({nLabel} sin clasificar)
                    </>
                  ) : (
                    <span style={{ color: "#adb5bd" }}>Todo el proveedor (sin proveedor identificado)</span>
                  )}
                </Radio>
              </div>
            </div>

            {reglaActual && esProveedor && (
              <div
                style={{
                  background: "#eafaf1",
                  border: "1px solid #abebc6",
                  color: "#1e8449",
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: "0.78rem",
                  marginBottom: 12,
                }}
              >
                Regla actual:{" "}
                <strong>
                  {reglaActual === MODO.GASTO && `Gasto → ${labelCuenta(prov.cuenta_er_default)}`}
                  {reglaActual === MODO.ACTIVO && "Activo"}
                  {reglaActual === MODO.HIBRIDO && "Híbrido (se clasifica factura por factura)"}
                </strong>
                . Cambiala abajo (re-propaga a las que sigan sin clasificar) o desvinculala.
              </div>
            )}

            {/* Texto explicativo dinámico según alcance. */}
            <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 12 }}>
              {esProveedor ? (
                <>
                  Se aplica a <strong>todas</strong> las facturas SII recibidas 2026 de este proveedor sin clasificar.
                  Nunca pisa una clasificación previa. Las futuras entran auto-clasificadas.
                </>
              ) : (
                <>
                  Vale <strong>solo para esta factura</strong>. No pisa la regla del proveedor.
                </>
              )}
            </div>

            {/* Naturaleza. Híbrido solo cuando el alcance es el proveedor. */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                {esProveedor ? "Naturaleza del proveedor" : "Clasificación de esta factura"}
              </label>
              <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", flexWrap: "wrap" }}>
                <Radio
                  name="modo"
                  value={MODO.GASTO}
                  checked={modo === MODO.GASTO}
                  onChange={() => setModo(MODO.GASTO)}
                >
                  Gasto
                </Radio>
                <Radio
                  name="modo"
                  value={MODO.ACTIVO}
                  checked={modo === MODO.ACTIVO}
                  onChange={() => setModo(MODO.ACTIVO)}
                >
                  Activo
                </Radio>
                {esProveedor && (
                  <Radio
                    name="modo"
                    value={MODO.HIBRIDO}
                    checked={modo === MODO.HIBRIDO}
                    onChange={() => setModo(MODO.HIBRIDO)}
                  >
                    Híbrido (mezcla)
                  </Radio>
                )}
              </div>
            </div>

            {modo === MODO.GASTO && (
              <div style={{ marginBottom: 4 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Cuenta del Estado de Resultados
                </label>
                <SelectorCuenta value={cuentaER} onChange={setCuentaER} />
              </div>
            )}

            {modo === MODO.ACTIVO && (
              <div
                style={{
                  background: "#eaf2f8",
                  color: "#2874a6",
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: "0.78rem",
                }}
              >
                {esProveedor
                  ? "Sus recibidas 2026 sin clasificar se marcan activo y salen de la línea de costo del ER."
                  : "Se marca como activo y sale de la línea de costo del ER."}{" "}
                El alta / depreciación del activo se hace aparte.
              </div>
            )}

            {esProveedor && modo === MODO.HIBRIDO && (
              <div style={{ border: "1px dashed #ccc", borderRadius: 6, padding: "10px 12px" }}>
                <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 8 }}>
                  El proveedor mezcla gasto y activo: no se propaga. Se clasifica <strong>solo esta factura</strong> y
                  sus futuras facturas no se auto-asignan.
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: "0.82rem", marginBottom: 8 }}>
                  <Radio
                    name="hibTipo"
                    value={MODO.GASTO}
                    checked={hibTipo === MODO.GASTO}
                    onChange={() => setHibTipo(MODO.GASTO)}
                  >
                    Esta = Gasto
                  </Radio>
                  <Radio
                    name="hibTipo"
                    value={MODO.ACTIVO}
                    checked={hibTipo === MODO.ACTIVO}
                    onChange={() => setHibTipo(MODO.ACTIVO)}
                  >
                    Esta = Activo
                  </Radio>
                </div>
                {hibTipo === MODO.GASTO && <SelectorCuenta value={hibCuenta} onChange={setHibCuenta} />}
              </div>
            )}
          </ModalBody>

          <ModalFooter style={{ justifyContent: "space-between" }}>
            <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
              {reglaActual && (
                <Button
                  color="link"
                  size="sm"
                  style={{ color: "#c0392b", padding: 0 }}
                  onClick={pedirDesvincular}
                  disabled={guardando}
                >
                  Desvincular regla
                </Button>
              )}
              {alcance === ALCANCE.FACTURA && clasificacionManual && (
                <Button
                  color="link"
                  size="sm"
                  style={{ color: "#c0392b", padding: 0 }}
                  onClick={volverALaRegla}
                  disabled={guardando}
                >
                  Volver a la regla del proveedor
                </Button>
              )}
            </span>
            <span>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={onClose}
                disabled={guardando}
                style={{ marginRight: 8 }}
              >
                Cancelar
              </Button>
              <Button color="success" size="sm" style={{ minWidth: 120 }} onClick={aplicar} disabled={guardando}>
                {guardando ? "Guardando…" : esProveedor ? "Aplicar" : "Aplicar excepción"}
              </Button>
            </span>
          </ModalFooter>
        </>
      )}

      {razonSocial && razonSocial !== proveedorNombre && !desvinculoPreview && (
        <div style={{ color: "#909497", fontSize: "0.72rem", padding: "0 16px 12px" }}>
          Razón social SII: {razonSocial}
        </div>
      )}
    </Modal>
  );
};

export default ClasificarModal;
